import React, { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { ensureAudio, playTone } from "../lib/sound";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Volume2, BellRing, Mail, Bell } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [
  { value: "normal", label: "All (Normal & up)" },
  { value: "important", label: "Important & Critical" },
  { value: "critical", label: "Critical only" },
];

function Row({ icon: Icon, title, desc, children, testid }) {
  return (
    <div data-testid={testid} className="flex items-center justify-between gap-4 rounded-md border border-slate-100 p-4">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 text-slate-400" />
        <div>
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          {desc && <p className="text-xs text-slate-500">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function NotificationPreferences() {
  const [p, setP] = useState(null);
  const [audioBlocked, setAudioBlocked] = useState(false);

  const load = () => api.get("/notifications/preferences").then((r) => setP(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    const next = { ...p, ...patch };
    setP(next);
    try {
      const { data } = await api.put("/notifications/preferences", patch);
      setP(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const enableAudibleOnDevice = () => {
    const ctx = ensureAudio();
    if (!ctx || ctx.state !== "running") {
      setAudioBlocked(true);
      toast.error("Your browser blocked audible alerts. Click Enable audible alerts to activate them.");
      return;
    }
    setAudioBlocked(false);
    playTone("important", p?.volume ?? 0.7);
    save({ audible: true, audible_device_enabled: true });
    toast.success("Audible alerts enabled on this device");
  };

  const testSound = () => {
    const ok = playTone(p?.muted ? "important" : (p?.audible_min_priority === "critical" ? "critical" : "important"), p?.volume ?? 0.7);
    if (!ok) {
      setAudioBlocked(true);
      toast.error("Your browser blocked audible alerts. Click Enable audible alerts to activate them.");
    }
  };

  if (!p) return <div className="h-64 animate-pulse rounded-md bg-slate-200/70" />;

  return (
    <div className="animate-fade-up max-w-3xl space-y-8">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Personal</p>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Notification Preferences</h1>
        <p className="mt-1 text-sm text-slate-500">Choose how WatchRelay alerts you about new and unacknowledged handoffs.</p>
      </div>

      <Card className="space-y-3 rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg font-bold text-slate-900">Channels</h2>
        <Row icon={Bell} title="In-app notifications" desc="Bell alerts for your assigned handoffs." testid="pref-in-app">
          <Switch data-testid="pref-in-app-switch" checked={!!p.in_app} onCheckedChange={(v) => save({ in_app: v })} />
        </Row>
        <Row icon={Mail} title="Email notifications" desc="Secure links only — no operational details." testid="pref-email">
          <Switch data-testid="pref-email-switch" checked={!!p.email} onCheckedChange={(v) => save({ email: v })} />
        </Row>
        <Row icon={BellRing} title="Audible alerts" desc="Play a subtle tone while WatchRelay is open." testid="pref-audible">
          <Switch data-testid="pref-audible-switch" checked={!!p.audible} onCheckedChange={(v) => save({ audible: v })} />
        </Row>
      </Card>

      <Card className="space-y-4 rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg font-bold text-slate-900">Thresholds & Reminders</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Minimum priority for email</Label>
            <Select value={p.email_min_priority} onValueChange={(v) => save({ email_min_priority: v })}>
              <SelectTrigger data-testid="pref-email-priority" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Minimum priority for audible</Label>
            <Select value={p.audible_min_priority} onValueChange={(v) => save({ audible_min_priority: v })}>
              <SelectTrigger data-testid="pref-audible-priority" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Row icon={BellRing} title="Supervisor escalation" desc="Escalate my unacknowledged handoffs to a supervisor." testid="pref-escalation">
          <Switch data-testid="pref-escalation-switch" checked={!!p.escalation_enabled} onCheckedChange={(v) => save({ escalation_enabled: v })} />
        </Row>
      </Card>

      <Card className="space-y-4 rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg font-bold text-slate-900">Sound on this device</h2>
        <p className="text-sm text-slate-500">
          Browsers require a click before playing sound. Enable audible alerts on this device to activate them.
        </p>
        {audioBlocked && (
          <p data-testid="audio-blocked-message" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Your browser blocked audible alerts. Click Enable audible alerts to activate them.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="enable-audible-button" onClick={enableAudibleOnDevice} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
            <Volume2 className="h-4 w-4" /> Enable audible alerts on this device
          </Button>
          <Button data-testid="test-sound-button" onClick={testSound} variant="outline" className="gap-2">
            Test sound
          </Button>
          <Button data-testid="mute-toggle-button" onClick={() => save({ muted: !p.muted })} variant="outline" className={p.muted ? "text-red-600 border-red-200" : ""}>
            {p.muted ? "Unmute" : "Mute"}
          </Button>
        </div>
        <div className="max-w-xs">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Volume</Label>
          <Slider
            data-testid="volume-slider"
            value={[Math.round((p.volume ?? 0.7) * 100)]}
            onValueChange={(v) => setP({ ...p, volume: v[0] / 100 })}
            onValueCommit={(v) => save({ volume: v[0] / 100 })}
            max={100}
            step={5}
            className="mt-3"
          />
        </div>
      </Card>
    </div>
  );
}
